import { useState, type FormEvent } from 'react';
import { useAuth } from 'react-oidc-context';
import { FormGroup, Label, Input, Button } from 'reactstrap';
import { useResetTenantAdminPassword, type TenantAdminMembership } from '../../api/platformTenants';
import { PasswordGenerator } from './PasswordGenerator';

interface Props {
  tenantId: number;
  admin: TenantAdminMembership;
  onClose: () => void;
}

export function TenantAdminResetPasswordModal({ tenantId, admin, onClose }: Props) {
  const auth = useAuth();
  const token = auth.user?.access_token ?? null;
  const mut = useResetTenantAdminPassword(tenantId, token);
  const [pwd, setPwd] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      await mut.mutateAsync({ userId: admin.user_id, temporary_password: pwd });
      setDone(true);
    } catch (err: unknown) {
      const e = err as { body?: { detail?: string }; message?: string };
      setError(e?.body?.detail ?? e?.message ?? 'Errore');
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,.4)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
      }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <form
        onSubmit={submit}
        style={{
          background: 'var(--vp-surface, #fff)', padding: 16, borderRadius: 8, minWidth: 400,
          display: 'flex', flexDirection: 'column', gap: 12,
        }}
      >
        <h5 className="fs-16 mb-2">
          Reset password — {admin.display_name ?? admin.email ?? `utente #${admin.user_id}`}
        </h5>
        <p style={{ fontSize: 12, color: 'var(--vp-text-2, #6b7280)', margin: 0 }}>
          La nuova password va consegnata manualmente all'utente. Al prossimo login dovrà cambiarla.
        </p>

        {!done && (
          <FormGroup>
            <Label for="rpm-pwd" className="mb-1">Nuova password temporanea (min 12 char)</Label>
            <div className="d-flex gap-2">
              <Input
                id="rpm-pwd"
                aria-label="Nuova password"
                type="text"
                value={pwd}
                onChange={(e) => setPwd(e.target.value)}
                minLength={12}
                required
                style={{ flex: 1 }}
              />
              <PasswordGenerator onGenerated={setPwd} value={pwd} />
            </div>
          </FormGroup>
        )}

        {done && (
          <div role="status" style={{
            padding: 8, background: 'var(--vp-success-soft, #cfe9d6)',
            borderRadius: 6, fontSize: 14,
          }}>
            Password resettata. Consegna la password all'utente: <code>{pwd}</code>
          </div>
        )}

        {error && <p role="alert" className="text-danger small mb-0">{error}</p>}

        <div className="d-flex justify-content-end gap-2">
          {done ? (
            <Button type="button" color="primary" size="sm" onClick={onClose}>Chiudi</Button>
          ) : (
            <>
              <Button type="button" color="light" size="sm" onClick={onClose}>Annulla</Button>
              <Button type="submit" color="primary" size="sm" disabled={mut.isPending}>
                {mut.isPending ? 'Reset in corso…' : 'Reset password'}
              </Button>
            </>
          )}
        </div>
      </form>
    </div>
  );
}
